import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Music, FileText, Shield, Plus, Crown, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const [planInfo, setPlanInfo] = useState(null);

  const loadPlanInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentPlan = response.data.find(p => p.current);
      setPlanInfo(currentPlan);
    } catch (error) {
      console.error('Error loading plan info:', error);
    }
  };

  useEffect(() => {
    loadPlanInfo();
  }, []);

  const getPlanLabel = (plan) => {
    const labels = {
      individual: 'Individual',
      professional: 'Profissional',
      premium: 'Premium'
    };
    return labels[plan] || plan;
  };

  const worksPercentage = planInfo && planInfo.max_works !== -1
    ? (planInfo.works_count / planInfo.max_works) * 100
    : 0;

  const isNearLimit = planInfo && planInfo.max_works !== -1 && worksPercentage >= 80;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_document-pro-2/artifacts/fsrl1cy2_Logo%20da%20Minha%20Composi%C3%A7%C3%A3o%203.png" 
              alt="Minha Composição" 
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-700">Olá, {user?.name}</span>
            <button onClick={logout} className="text-red-600 hover:text-red-700">
              Sair
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Dashboard</h1>
        
        {/* Plan Alert */}
        {isNearLimit && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
                <div>
                  <p className="font-semibold text-yellow-800">
                    Você está próximo do limite do seu plano!
                  </p>
                  <p className="text-sm text-yellow-700">
                    {planInfo.works_count} de {planInfo.max_works} obras utilizadas ({worksPercentage.toFixed(0)}%)
                  </p>
                </div>
              </div>
              <Link
                to="/plans"
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold transition-all"
              >
                Fazer Upgrade
              </Link>
            </div>
          </div>
        )}
        
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Works Count Card */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <FileText className="w-12 h-12 text-blue-600 mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{user?.works_count || 0}</h3>
            <p className="text-gray-600 mb-3">Obras Registradas</p>
            {planInfo && planInfo.max_works !== -1 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Limite: {planInfo.max_works}</span>
                  <span>{worksPercentage.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      isNearLimit ? 'bg-yellow-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${Math.min(worksPercentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          
          {/* Plan Card */}
          <div className="bg-gradient-to-br from-purple-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
            <Crown className="w-12 h-12 mb-4" />
            <h3 className="text-2xl font-bold mb-2">
              {getPlanLabel(user?.plan || 'individual')}
            </h3>
            <p className="opacity-90 mb-4">Plano Atual</p>
            <Link
              to="/plans"
              className="inline-block bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Ver Planos
            </Link>
          </div>
          
          {/* New Work Card */}
          <Link to="/works/new" className="bg-gradient-primary text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all flex flex-col items-center justify-center group">
            <Plus className="w-12 h-12 mb-4 group-hover:scale-110 transition-transform" />
            <p className="text-xl font-semibold">Registrar Nova Obra</p>
          </Link>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ações Rápidas</h2>
          <div className="space-y-3">
            <Link to="/works" className="block p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              📚 Ver Minhas Obras
            </Link>
            <Link to="/profile" className="block p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
              👤 Editar Perfil
            </Link>
            {(user?.plan === 'master' || user?.status === 'master') && (
              <Link to="/admin" className="block p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border-2 border-red-200">
                🔐 <strong>PAINEL ADMINISTRATIVO</strong>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
